import { useState, useEffect } from 'react'
import './Leaderboard.css'
import { API_URL } from './config/api'

interface LeaderboardProps {
  language: 'zh' | 'en'
}

interface LeaderboardEntry {
  rank: number
  address: string
  total_points: number
  total_deposits: number
}

interface GlobalStats {
  total_users: number
  total_deposits: number
  total_points: number
}

export function Leaderboard({ language }: LeaderboardProps) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [stats, setStats] = useState<GlobalStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const t = {
    zh: {
      title: '积分排行榜',
      globalStats: '全局统计',
      totalUsers: '总用户数',
      totalDeposits: '总存款笔数',
      totalPoints: '总积分',
      rank: '排名',
      address: '地址',
      points: '积分',
      deposits: '存款次数',
      loading: '加载中...',
      error: '加载失败',
      noData: '暂无数据',
      top100: '前 100 名',
      you: '你',
    },
    en: {
      title: 'Leaderboard',
      globalStats: 'Global Stats',
      totalUsers: 'Total Users',
      totalDeposits: 'Total Deposits',
      totalPoints: 'Total Points',
      rank: 'Rank',
      address: 'Address',
      points: 'Points',
      deposits: 'Deposits',
      loading: 'Loading...',
      error: 'Failed to load',
      noData: 'No data',
      top100: 'Top 100',
      you: 'You',
    }
  }

  const content = t[language]

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError('')

    try {
      // 加载排行榜
      const leaderboardRes = await fetch(`${API_URL}/api/leaderboard?limit=100`)
      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json()
        setLeaderboard(data)
      }

      // 加载全局统计
      const statsRes = await fetch(`${API_URL}/api/stats`)
      if (statsRes.ok) {
        const data = await statsRes.json()
        setStats(data)
      }

    } catch (err) {
      console.error('加载排行榜失败:', err)
      setError(content.error)
    } finally {
      setLoading(false)
    }
  }

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(Math.floor(num))
  }

  if (loading) {
    return (
      <div className="leaderboard">
        <div className="loading-state">{content.loading}</div>
      </div>
    )
  }

  return (
    <div className="leaderboard">
      <h2 className="leaderboard-title">🏆 {content.title}</h2>

      {/* 全局统计 */}
      {stats && (
        <div className="global-stats">
          <h3>{content.globalStats}</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-label">{content.totalDeposits}</div>
                <div className="stat-value">{formatNumber(stats.total_deposits)}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <div className="stat-label">{content.totalUsers}</div>
                <div className="stat-value">{formatNumber(stats.total_users)}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✨</div>
              <div className="stat-info">
                <div className="stat-label">{content.totalPoints}</div>
                <div className="stat-value">{formatNumber(stats.total_points)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 排行榜 */}
      <div className="leaderboard-section">
        <h3>{content.top100}</h3>

        {error && (
          <div className="error-message">{error}</div>
        )}

        {!error && leaderboard.length === 0 && (
          <div className="no-data">{content.noData}</div>
        )}

        {!error && leaderboard.length > 0 && (
          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>{content.rank}</th>
                  <th>{content.address}</th>
                  <th>{content.points}</th>
                  <th>{content.deposits}</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr
                    key={entry.rank}
                    className={entry.rank <= 3 ? `top-${entry.rank}` : ''}
                  >
                    <td className="rank-cell">
                      {entry.rank === 1 && '🥇'}
                      {entry.rank === 2 && '🥈'}
                      {entry.rank === 3 && '🥉'}
                      {entry.rank > 3 && `#${entry.rank}`}
                    </td>
                    <td className="address-cell">
                      <code>{formatAddress(entry.address)}</code>
                    </td>
                    <td className="points-cell">
                      <span className="points-value">
                        {formatNumber(entry.total_points)}
                      </span>
                    </td>
                    <td className="deposits-cell">
                      {entry.total_deposits}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
